/**
 * Optimistic patch for the `pending-payments` aggregate cache.
 *
 * Mirrors the state-machine cascade `applyAttach` / `applyMarkPaid` enforce on
 * the server side (see `model/txn-status.ts`), but operates on the projected
 * `{ summary, groups }` snapshot the page consumes — NOT on the raw ledger.
 * Server-truth vs client-preview parity is kept by re-using the same status
 * vocabulary; drift would surface immediately because both sides parse the
 * same Zod schemas.
 *
 * Status cascade after a CREATE attach:
 *   - paid:false → covered txns UNPAID → INVOICED
 *     · drops them from the active UNPAID `groups` entry
 *     · decrements card #1 (NON_PAYABLE_EXPENSES) or #2 (PENDING_INCOME)
 *     · increments card #3 (PAYMENT_OPERATION) IFF kind is EXPENSES
 *       (PAYMENT_OPERATION is EXPENSE∧INVOICED — INCOME never enters)
 *   - paid:true  → covered txns UNPAID → SETTLED (server cascade is the same
 *     as PATCH /invoices/{id}/paid)
 *     · drops them from the active UNPAID `groups` entry
 *     · decrements card #1 / #2 (same as above)
 *     · card #3 stays unchanged (SETTLED rows are out of the queue entirely)
 *
 * Pure + deterministic so unit tests can assert the patch shape without a
 * QueryClient. The mutation hook composes this with snapshot/rollback.
 */

import type {
  PendingGroup,
  PendingPaymentsResponse,
  PendingSummaryCard,
  Transaction,
} from '../api/contracts'

export type PendingKind = 'EXPENSES' | 'INCOME'

export interface OptimisticAttachInput {
  kind: PendingKind
  txnIds: readonly string[]
  paid: boolean
}

/**
 * Patch the pending-payments cache after a successful (or in-flight) CREATE
 * attach. Returns a new response object; the input is not mutated.
 *
 * When `txnIds` references rows that aren't in the active group (already
 * removed by a sibling tab, race condition), the corresponding patch terms
 * collapse to zero — the function is idempotent under that race instead of
 * subtracting amounts that aren't there.
 */
export function applyOptimisticAttach(
  cache: PendingPaymentsResponse,
  { kind, txnIds, paid }: OptimisticAttachInput,
): PendingPaymentsResponse {
  if (txnIds.length === 0) return cache

  const txnIdSet = new Set(txnIds)
  const activeGroup = cache.groups.find((g) => g.type === kind)
  const removed: Transaction[] = activeGroup?.items.filter((t) => txnIdSet.has(t.id)) ?? []
  if (removed.length === 0) return cache

  const removedCount = removed.length
  const removedSum = removed.reduce((s, t) => s + t.amount, 0)

  const nextGroups: PendingGroup[] = cache.groups.map((g) => {
    if (g.type !== kind) return g
    const nextItems = g.items.filter((t) => !txnIdSet.has(t.id))
    return { ...g, items: nextItems, totalCount: nextItems.length }
  })

  const nextSummary: PendingSummaryCard[] = cache.summary.map((card) => {
    if (
      (kind === 'EXPENSES' && card.status === 'NON_PAYABLE_EXPENSES') ||
      (kind === 'INCOME' && card.status === 'PENDING_INCOME')
    ) {
      return {
        ...card,
        totalCount: Math.max(0, card.totalCount - removedCount),
        totalSum: Math.max(0, card.totalSum - removedSum),
      }
    }
    if (card.status === 'PAYMENT_OPERATION' && !paid && kind === 'EXPENSES') {
      return {
        ...card,
        totalCount: card.totalCount + removedCount,
        totalSum: card.totalSum + removedSum,
      }
    }
    return card
  })

  return { summary: nextSummary, groups: nextGroups }
}
